"use client";

import { type MindstringPrototype, type Target } from "@/generated/fastapi";
import { Box } from "@chakra-ui/react";
import type { Dispatch, SetStateAction } from "react";
import { memo, useCallback } from "react";
import { useUserChoices } from "@/state/globalStore";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import LoadingState from "@/shared/ui/LoadingState";
import {
  PROFILE_SHAPING_EMPTY_STATES,
  PROFILE_SHAPING_LOADING_STATES,
} from "@/features/profile-shaping/config/actions";
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { useSystemDesign } from "@/shared/system-design";
import useStudioFreeListArrangement from "./useStudioFreeListArrangement";
import useSortableStudioThoughts from "../../hooks/useSortableStudioThoughts";
import SortableStudioThoughtItem from "./SortableStudioThoughtItem";
import { syncSelectedThoughtsFreeOrderIfChanged } from "../../utils";
import { getSameListReorderResult } from "./arrangementReorder";
import type { SelectedThoughtDocument } from "../../types";

interface StudioFreeListArrangementProps {
  prototype: MindstringPrototype;
  rerankTarget?: Target | null | undefined;
}

function useDragAndDrop({
  sortedThoughts,
  setSortedThoughts,
  prototype,
  setHasManuallyReordered,
  isMobileDevice,
}: {
  sortedThoughts: SelectedThoughtDocument[];
  setSortedThoughts: Dispatch<SetStateAction<SelectedThoughtDocument[]>>;
  prototype: MindstringPrototype;
  setHasManuallyReordered: Dispatch<SetStateAction<boolean>>;
  isMobileDevice: boolean;
}) {
  const { reorderFreeThoughtSelection } = useUserChoices();
  const { setIsDragging, sensors } = useSortableStudioThoughts({
    isMobileDevice,
  });

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      const reorderResult = getSameListReorderResult(
        sortedThoughts,
        active.id,
        over?.id,
      );

      if (!reorderResult) {
        return;
      }

      setSortedThoughts(reorderResult.reorderedThoughts);
      setHasManuallyReordered(true);

      syncSelectedThoughtsFreeOrderIfChanged(
        sortedThoughts.map((thought) => thought.objectID),
        reorderResult.orderedThoughtIds,
        (orderedThoughtIds) =>
          reorderFreeThoughtSelection({
            prototypeCategory: prototype,
            orderedThoughtIds,
          }),
      );
    },
    [
      prototype,
      reorderFreeThoughtSelection,
      setHasManuallyReordered,
      setSortedThoughts,
      sortedThoughts,
    ],
  );

  return {
    setIsDragging,
    sensors,
    handleDragEnd,
  };
}

function StudioFreeListArrangementInner({
  prototype,
  rerankTarget,
}: StudioFreeListArrangementProps) {
  const { isMobileDevice } = useSystemDesign();
  const { getSelectedThoughtsFree } = useUserChoices();
  const selectedThoughtsFreeForArrangement = getSelectedThoughtsFree(prototype);
  const arrangementScopeKey = [
    "free-list",
    prototype,
    rerankTarget?.id ?? rerankTarget?.content ?? "no-rerank-target",
  ].join(":");

  const {
    isLoading,
    sortedThoughts,
    setSortedThoughts,
    setHasManuallyReordered,
  } = useStudioFreeListArrangement({
    selectedThoughtsFree: selectedThoughtsFreeForArrangement,
    rerankTarget,
    scopeKey: arrangementScopeKey,
  });

  const { setIsDragging, sensors, handleDragEnd } = useDragAndDrop({
    sortedThoughts,
    setSortedThoughts,
    prototype,
    setHasManuallyReordered,
    isMobileDevice,
  });

  if (selectedThoughtsFreeForArrangement.length === 0) {
    return (
      <GenericEmptyComponent
        title={PROFILE_SHAPING_EMPTY_STATES.orderMode.title}
        message={PROFILE_SHAPING_EMPTY_STATES.orderMode.message}
        variant="inline"
      />
    );
  }

  if (isLoading) {
    return (
      <LoadingState
        loadingMessage={PROFILE_SHAPING_LOADING_STATES.ordering.loadingMessage}
        message={PROFILE_SHAPING_LOADING_STATES.ordering.message}
        variant="inline"
      />
    );
  }

  if (sortedThoughts.length === 0) {
    return (
      <GenericEmptyComponent
        title={PROFILE_SHAPING_EMPTY_STATES.confirmedThoughts.title}
        message={PROFILE_SHAPING_EMPTY_STATES.confirmedThoughts.message}
        variant="inline"
      />
    );
  }

  return (
    <Box userSelect="none">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={(event) => {
          setIsDragging(false);
          handleDragEnd(event);
        }}
        onDragCancel={() => setIsDragging(false)}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <SortableContext
          items={sortedThoughts.map((thought) => thought.objectID)}
          strategy={verticalListSortingStrategy}
        >
          {sortedThoughts.map((thought) => (
            <SortableStudioThoughtItem
              key={thought.objectID}
              thought={thought}
              isMobile={isMobileDevice}
              prototype={prototype}
              selectionSituationId={thought.situation_id ?? null}
              textMinH="6"
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
}

const StudioFreeListArrangement = memo(StudioFreeListArrangementInner);
export default StudioFreeListArrangement;
